import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Children, forwardRef, isValidElement, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
  HTMLDivElement,
  DialogPrimitive.DialogOverlayProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

interface DialogContentProps extends DialogPrimitive.DialogContentProps {
  hideCloseButton?: boolean;
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  ({ className, children, hideCloseButton, ...props }, ref) => {
    // Layout-Fix: Header + Footer bleiben fix, NUR der Body scrollt. Vorher scrollte der ganze
    // Dialog und der `sticky` Footer schwebte über dem Inhalt — bei knappem Viewport verdeckte die
    // opake Footer-Leiste die letzte Zeile (z.B. Hilfetext). Wir teilen die Kinder in Header/Body/
    // Footer, ohne dass die 28 Dialoge etwas ändern müssen (sie liefern DialogHeader + Body +
    // DialogFooter als Geschwister). Der Body-Bereich bekommt overflow-y-auto + flex-1.
    const kids = Children.toArray(children);
    const header = kids.find((c) => isValidElement(c) && c.type === DialogHeader);
    const footer = kids.find((c) => isValidElement(c) && c.type === DialogFooter);
    const body = kids.filter((c) => c !== header && c !== footer);
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            // flex-col + max-h + overflow-hidden: der Content-Container clippt nicht, sondern der
            // innere Body scrollt. Header/Footer sind shrink-0 → immer sichtbar (Buttons bleiben).
            "fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            className,
          )}
          {...props}
        >
          {header && <div className="shrink-0 px-6 pt-6">{header}</div>}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">{body}</div>
          {footer && <div className="shrink-0">{footer}</div>}
          {!hideCloseButton && (
            <DialogPrimitive.Close
              className="absolute right-4 top-4 rounded opacity-70 hover:opacity-100 focus:outline-none"
              data-testid="dialog-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = "DialogContent";

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col gap-1.5", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // Fixe Fußleiste (kein sticky mehr): DialogContent legt Header/Body/Footer als flex-col an,
      // der Body scrollt eigenständig → der Footer sitzt immer unten + überlappt nie den Inhalt.
      // Trennlinie oben + volle Breite (px-6/pb-6 = das alte Container-p-6).
      className={cn(
        "flex flex-col-reverse border-t border-border bg-surface px-6 pb-6 pt-3 sm:flex-row sm:justify-end sm:gap-2",
        className,
      )}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<HTMLHeadingElement, DialogPrimitive.DialogTitleProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-[17px] font-bold tracking-tight text-text", className)}
      {...props}
    />
  ),
);
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = forwardRef<
  HTMLParagraphElement,
  DialogPrimitive.DialogDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-subtext0", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

export function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("space-y-4", className)}>{children}</div>;
}
